// Copyright 2025, University of Maryland, All Rights Reserved
import type { PipelineNodeData } from '../../../core/contracts'
import type { WorkflowNode, WorkflowNodeUIMetadata, WorkflowNodeConfig } from '../../pipelines'

// Top-level WorkflowNode fields that live outside uiMetadata/config
// nodeInstanceId is legacy - filter it out so it doesn't pollute settings
const TOP_LEVEL_KEYS: Set<string> = new Set(['nodeDefinition', 'instanceId', 'nodeInstanceId'])

// Keys that belong in uiMetadata (UI-only) vs config (passed to node)
const UI_METADATA_KEYS: Set<string> = new Set(['label', 'configured'])

/** Top-level WorkflowNode fields extracted during split */
export interface WorkflowNodeTopLevel {
  nodeDefinition?: string
  instanceId?: string
}

// Flattens node.uiMetadata + node.config + top-level fields into single PipelineNodeData for ReactFlow
export const mergeWorkflowNodeData = (node: WorkflowNode): PipelineNodeData => ({
  ...node.uiMetadata,
  ...node.config,
  nodeDefinition: node.nodeDefinition ?? undefined,
  instanceId: node.instanceId ?? undefined,
}) as PipelineNodeData

// Splits flat PipelineNodeData back into topLevel + uiMetadata + config for API persistence
export const splitNodeDataForPersistence = (
  nodeData: PipelineNodeData
): { topLevel: WorkflowNodeTopLevel; uiMetadata: WorkflowNodeUIMetadata; config: WorkflowNodeConfig } => {
  const { config: nestedConfig, ...rest } = nodeData
  const topLevel: Record<string, unknown> = {}
  const uiMetadata: Record<string, unknown> = {}
  const config: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(rest)) {
    if (TOP_LEVEL_KEYS.has(key)) {
      topLevel[key] = value
    } else if (UI_METADATA_KEYS.has(key)) {
      uiMetadata[key] = value
    } else {
      config[key] = value
    }
  }

  // Nested config overwrites top-level duplicates
  if (nestedConfig && typeof nestedConfig === 'object') {
    Object.assign(config, nestedConfig)
  }

  return { topLevel: topLevel as WorkflowNodeTopLevel, uiMetadata, config }
}
